import type { TablesSummary, LayoutSummary, QueueSummary } from '@/api/dashboardSummary';
import type { ChecklistDto } from '@/api/ops_tasks';
import type { Paginated } from '@/api/types';

// SINGLE SOURCE OF TRUTH for «is this monitored component active in this
// branch». Every surface that gates a component (shift dashboard, Статистика,
// reports, the all-branches dashboard) must derive the answer from here —
// duplicated ad-hoc checks already produced the «checklists shown for
// branches without checklists» bug (Alexey, 2026-07-19).
//
// The all-branches batch endpoint carries the same semantics server-side:
// PortfolioZoneDashboardDto.checklists_configured mirrors
// checklistsConfigured(), and empty racks/tables/queues arrays mirror the
// summary-based predicates below (BE services/api/portfolio.py).

/** Zone types shown to customers by default — real branches, not service
 *  zones. ONE list for the branch selector, the all-branches boards, and any
 *  other customer-facing zone enumeration. */
export const RETAIL_ZONE_TYPES = ['RETAIL_BRANCH', 'COFFEESHOP', 'SHOP', 'CANTEEN'] as const;

export const isRetailZone = (type?: string | null): boolean =>
  !type || (RETAIL_ZONE_TYPES as readonly string[]).includes(type.toUpperCase());

/** Canonical rack-category display order (Alexey, 2026-07-19): fresh
 *  high-margin first, auxiliary after, anything unknown last; alphabetical
 *  within a rank. ONE order for the day mini cards, dashboard dot rows and
 *  any future category enumeration. */
export const RACK_CATEGORY_ORDER = ['Готовая еда', 'Выпечка', 'Десерты', 'Сопутствующая продукция'] as const;

export const rackCategoryRank = (type?: string | null): number => {
  const i = (RACK_CATEGORY_ORDER as readonly string[]).indexOf((type ?? '').trim());
  return i === -1 ? RACK_CATEGORY_ORDER.length : i;
};

/** Table cleanliness is monitored: the zone has at least one table. */
export const tablesConfigured = (s: TablesSummary | undefined): boolean =>
  !!s && s.total > 0;

/** Layout (выкладка) is monitored: the zone has at least one enabled rack. */
export const layoutConfigured = (s: LayoutSummary | undefined): boolean =>
  !!s && s.total > 0;

/** Queues are monitored: the checkout_control plugin reports configured. */
export const queuesConfigured = (s: QueueSummary | undefined): boolean =>
  !!s && s.configured;

/** Checklists are active: at least one checklist carries ≥1 task item. A
 *  checklist row with zero items (e.g. an empty sheet) does NOT count. */
export const checklistsConfigured = (
  checklists: Paginated<ChecklistDto> | undefined,
): boolean =>
  !!checklists && checklists.results.some(c => (c.items?.length ?? 0) > 0);
